import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { timesheetAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Clock, 
  Plus, 
  CheckCircle, 
  XCircle,
  Calendar,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const Timesheets = () => {
  const { user } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    week_start: '',
    week_end: '',
    hours: {
      monday: 8,
      tuesday: 8,
      wednesday: 8,
      thursday: 8,
      friday: 8,
      saturday: 0,
      sunday: 0
    },
    notes: ''
  });

  const isHR = user?.role === 'supervisor' || user?.role === 'executive';

  useEffect(() => {
    fetchTimesheets();
  }, []);

  const fetchTimesheets = async () => {
    try {
      setLoading(true);
      const response = await timesheetAPI.getAll();
      setTimesheets(response.data);
      
      if (isHR) {
        const pendingRes = await timesheetAPI.getPending();
        setPendingTimesheets(pendingRes.data);
      }
    } catch (error) {
      console.error('Timesheet fetch error:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.week_start || !formData.week_end) {
      toast.error('Please select week dates');
      return;
    }

    try {
      await timesheetAPI.create(formData);
      toast.success('Timesheet submitted for approval!');
      setShowDialog(false);
      setFormData({
        week_start: '',
        week_end: '',
        hours: { monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8, saturday: 0, sunday: 0 },
        notes: ''
      });
      fetchTimesheets();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit timesheet');
    }
  };

  const handleApprove = async (id) => {
    try {
      await timesheetAPI.approve(id);
      toast.success('Timesheet approved!');
      fetchTimesheets();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Failed to approve timesheet');
    }
  };

  const handleHoursChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      hours: { ...prev.hours, [day]: parseFloat(value) || 0 }
    }));
  };

  const totalHours = Object.values(formData.hours).reduce((sum, h) => sum + h, 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading timesheets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Submitted</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {timesheets.length}
                </p>
              </div>
              <FileText className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending Approval</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {timesheets.filter(t => t.status === 'pending').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Approved</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {timesheets.filter(t => t.status === 'approved').length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* HR Pending Approvals */}
      {isHR && pendingTimesheets.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Pending Approvals ({pendingTimesheets.length})
            </CardTitle>
            <CardDescription className="text-amber-600">Review and approve employee timesheets</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingTimesheets.map((ts) => (
                <div key={ts.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-slate-900">Employee {ts.employee_id}</p>
                    <p className="text-sm text-slate-500">{ts.week_start} to {ts.week_end}</p>
                    <p className="text-sm text-slate-600">{ts.total_hours} hours total</p>
                  </div>
                  <Button onClick={() => handleApprove(ts.id)} className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timesheets List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>My Timesheets</CardTitle>
              <CardDescription>Submit and track your weekly hours</CardDescription>
            </div>
            <Button 
              onClick={() => setShowDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="new-timesheet-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Timesheet
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {timesheets.length > 0 ? (
            <div className="space-y-4">
              {timesheets.map((ts) => (
                <div key={ts.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        Week: {ts.week_start} to {ts.week_end}
                      </p>
                      <p className="text-sm text-slate-500">
                        Total: {ts.total_hours} hours
                      </p>
                      {ts.notes && <p className="text-xs text-slate-400 mt-1">{ts.notes}</p>}
                    </div>
                  </div>
                  {getStatusBadge(ts.status)}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Timesheets</h3>
              <p className="text-slate-500 mb-4">Start tracking your work hours</p>
              <Button onClick={() => setShowDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Submit Timesheet
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Timesheet Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Submit Timesheet</DialogTitle>
            <DialogDescription>Enter your hours for the week</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Week Start</Label>
                <Input
                  type="date"
                  value={formData.week_start}
                  onChange={(e) => setFormData(prev => ({ ...prev, week_start: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Week End</Label>
                <Input
                  type="date"
                  value={formData.week_end}
                  onChange={(e) => setFormData(prev => ({ ...prev, week_end: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Daily Hours</Label>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex items-center justify-between">
                  <span className="capitalize text-slate-600 w-24">{day}</span>
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    step="0.5"
                    value={formData.hours[day]}
                    onChange={(e) => handleHoursChange(day, e.target.value)}
                    className="w-24"
                  />
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <span className="font-semibold text-slate-900">Total Hours</span>
                <span className="font-bold text-indigo-600 text-lg">{totalHours}h</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                placeholder="Any comments about this week..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Timesheets;
